/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import {
  Sidebar,
  Button,
  Label,
  MultipleSelect,
  SelectButton,
} from "@nordcloud/gnui";
import {
  useGlobalTagsQuery,
  useUpdateGlobalKeysMutation,
} from "~/generated/graphql";
import { Fieldset, InputField } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showSuccess, showError } from "~/services/toast";

const schema = yup.object().shape({
  mappingRule: yup.string().required(),
});

type Props = {
  onKeyAdd: () => void;
};

type FormData = {
  mappingRule: string;
};

export function MappingRuleSideButton({ onKeyAdd }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [value, setAppValue] = React.useState("application");

  const { data } = useGlobalTagsQuery();
  const [updateKeys] = useUpdateGlobalKeysMutation();

  const {
    register,
    handleSubmit,
    reset,

    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (formData: FormData) => {
    setIsSidebarOpen(false);
    try {
      await addKey(value)(formData.mappingRule);
      showSuccess(SUCCESS_TEXT.mappingRuleAdded);

      onKeyAdd();
    } catch {
      showError(ERROR_TEXT.default);
    } finally {
      reset();
    }
  };

  const addKey = (kind: string) => (rule: string) => {
    const { application = [], environment = [] } = data?.globalTagKeys || {};
    if (kind === "application") {
      return updateKeys({
        variables: {
          application: [...application, rule],
          environment,
        },
      });
    }
    return updateKeys({
      variables: {
        application,
        environment: [...environment, rule],
      },
    });
  };

  return (
    <>
      <Button
        icon="plus"
        onClick={() => {
          setIsSidebarOpen(true);
        }}
      >
        Add Mapping Rule
      </Button>

      <Sidebar
        title="Add Mapping Rule"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset>
            <Label name="Choose Application or Environment" />
            <div style={{ display: "flex" }}>
              <MultipleSelect>
                <SelectButton
                  name="application"
                  value="application"
                  labelText="Application"
                  isActive={value === "application"}
                  onClick={() => {
                    setAppValue("application");
                  }}
                />
                <SelectButton
                  name="environment"
                  value="environment"
                  labelText="Environment"
                  isActive={value === "environment"}
                  onClick={() => {
                    setAppValue("environment");
                  }}
                />
              </MultipleSelect>
            </div>
          </Fieldset>
          <Fieldset>
            <InputField
              label="Tag key"
              error={errors.mappingRule}
              {...register("mappingRule", {
                required: true,
              })}
              required
            />
          </Fieldset>
          <Button type="submit">Add</Button>
        </form>
      </Sidebar>
    </>
  );
}
